import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment';

import { ObjectUtils } from '../../../utils/object.utils';
import { CustomValidators } from '../models/CustomValidators';
import { Control, TypeSafeFormArray, TypeSafeFormControl, TypeSafeFormGroup } from '../models/TypeSafeForms';
import { FormUtils } from 'src/app/shared/utils/form.utils';

export abstract class FormCreatorService {

  safeRemoveFromArray(source: UntypedFormArray, toRemove: AbstractControl): void {
    if (!source || !toRemove) {
      return;
    }

    const index = source.controls.findIndex(control => control === toRemove);
    if (index >= 0) {
      source.removeAt(index);
    }
  }

  createOrigLineArrayValues(origLine: UntypedFormGroup, line: UntypedFormGroup, linePath: string, createFormGroupFn: () => UntypedFormGroup) {
    let arrayCount = (<UntypedFormArray>line.get(linePath)).length;
    FormUtils.clearFormArray(origLine.get(linePath) as UntypedFormArray);
    while (arrayCount > 0) {
      (<UntypedFormArray>origLine.get(linePath)).push(createFormGroupFn());
      arrayCount--;
    }
  }

  hasError(form: UntypedFormGroup | UntypedFormArray, error: string, formError: {hasError: boolean}): boolean {
    if (!formError.hasError) {
      Object.keys(form.controls).forEach(field => {
        const control = form.get(field);
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          return this.hasError(control, error, formError);
        } else if (control instanceof UntypedFormControl) {
          if (control.errors && control.errors[error]) {
            formError.hasError = true;
          }
        }
      });
    }

    return formError.hasError;
  }

  hasAnyError(form: UntypedFormGroup | UntypedFormArray, formError: {hasError: boolean}): boolean {
    if (!formError.hasError) {
      Object.keys(form.controls).forEach(field => {
        const control = form.get(field);
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          return this.hasAnyError(control, formError);
        } else if (control instanceof UntypedFormControl) {
          if (control.errors) {
            formError.hasError = true;
          }
        }
      });
    }

    return formError.hasError;
  }

  protected createAbstractControl(shape: any, value: any, validator: CustomValidators<any>): AbstractControl {
    if (ObjectUtils.isArray(shape)) {
      return this.createFormArray(shape, value, validator);
    } else if (ObjectUtils.isObject(shape) && !ObjectUtils.isDate(shape)) {
      return this.createFormGroup(shape, value, validator);
    } else {
      return this.createFormControl(shape, value, validator);
    }
  }

  protected createFormArray(shapes: Array<any>, values: Array<any>, arrayValidator: CustomValidators<any[]>): UntypedFormArray {
    const [shape] = shapes;
    arrayValidator = arrayValidator || {};
    const [validator] = arrayValidator.children || [null];

    if (ObjectUtils.isArray(values) && values.length) {
      return new UntypedFormArray(values.map(value => this.createAbstractControl(shape, value, validator)), arrayValidator.validators);
    } else if (shapes['inline']) {
      return new UntypedFormArray([this.createAbstractControl(shape, null, validator)], arrayValidator.validators);
    }
    return new UntypedFormArray([], arrayValidator.validators);
  }

  public createFormGroup<T>(shape: T, value: T, validator: CustomValidators<T>): UntypedFormGroup {
    shape = shape || { } as any;
    validator = validator || { validators: null, children: {}} as any;
    if (!validator.children) {
      validator.children = {} as any;
    }
    const form = Object.keys(shape)
      .map((key: string) => [this.getControl(key, shape[key], value && value[key], validator.children[key]), key])
      .reduce((obj: object, [control, key]: [AbstractControl, string]) => {
        obj[key] = control;
        return obj;
      }, {});

    return new UntypedFormGroup(form, validator.validators);
  }

  public createFormControl(shape: any, value: any, validator: CustomValidators<any>): UntypedFormControl {
    return new UntypedFormControl(value != null ? value : shape, validator && validator.validators);
  }

  protected getControl(key: string, shapeObj: any, valueObj: any, validator: CustomValidators<any>): AbstractControl {
    return this.createAbstractControl(shapeObj, valueObj, validator);
  }

  protected findValidators(...path: string[]): CustomValidators<any> {
    let value: CustomValidators<any> = this.getValidators(true);
    if (!value) {
      return null;
    }

    for (const key of path) {
      if (value && value.children && value.children.hasOwnProperty(key)) {
        value = value.children[key];
      } else {
        console.error(`No validator with specified path: '${path.join('.')}'`);
        return null;
      }
    }
    return value;
  }

  protected abstract getValidators(editMode?: boolean): CustomValidators<any>;
}

export abstract class TypeSafeFormCreatorService<T extends object> {

  protected createAbstractControl<V = any>(shape: V, value: V, validator: CustomValidators<V>): Control<V> {
    if (ObjectUtils.isArray(shape)) {
      return this.createFormArray<any>(shape, value, validator) as any;
    } else if (ObjectUtils.isObject(shape) && !ObjectUtils.isDate(shape)) {
      return this.createFormGroup<any>(shape, value, validator) as any;
    } else {
      return this.createFormControl<any>(shape, value, validator) as any;
    }
  }

  protected createFormArray<V extends any[]>(shapes: V, values: V, arrayValidator: CustomValidators<V>): TypeSafeFormArray<V> {
    const [shape] = shapes;
    arrayValidator = arrayValidator || {};
    const [validator] = arrayValidator.children || [null];

    if (ObjectUtils.isArray(values) && values.length) {
      return new TypeSafeFormArray(values.map(value => this.createAbstractControl(shape, value, validator)), arrayValidator.validators);
    } else if (shapes['inline']) {
      return new TypeSafeFormArray([this.createAbstractControl(shape, null, validator)], arrayValidator.validators);
    }
    return new TypeSafeFormArray([], arrayValidator.validators);
  }

  public createFormGroup<Y extends object = T>(shape: Y, value: Y, validator: CustomValidators<Y>): TypeSafeFormGroup<Y> {
    shape = shape || { } as any;
    validator = validator || { validators: null, children: {}} as any;
    if (!validator.children) {
      validator.children = {} as any;
    }
    const form = Object.keys(shape)
      .map((key: string) => [this.getControl(key, shape[key], value && value[key], validator.children[key]), key])
      .reduce((obj: object, [control, key]: [AbstractControl, string]) => {
        obj[key] = control;
        return obj;
      }, {});

    return new TypeSafeFormGroup(form, validator.validators);
  }

  public createFormControl<V extends number | string | boolean | moment.Moment>(shape: V, value: V, validator: CustomValidators<V>): TypeSafeFormControl<V> {
    return new TypeSafeFormControl<V>(value != null ? value : shape, validator && validator.validators);
  }

  protected getControl(key: string, shapeObj: any, valueObj: any, validator: CustomValidators<any>): AbstractControl {
    return this.createAbstractControl(shapeObj, valueObj, validator);
  }

  /* tslint:disable:max-line-length */
  protected findValidators<K extends keyof T>(k: K): CustomValidators<T[K]>;
  protected findValidators<K extends keyof T, K2 extends keyof T[K]>(k: K, k2: K2): CustomValidators<T[K][K2]>;
  protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2]>(k: K, k2: K2, k3: K3): CustomValidators<T[K][K2][K3]>;
  protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3]>(k: K, k2: K2, k3: K3, k4: K4): CustomValidators<T[K][K2][K3][K4]>;
  protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5): CustomValidators<T[K][K2][K3][K4][K5]>;
  // protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6): CustomValidators<T[K][K2][K3][K4][K5][K6]>;
  // protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7): CustomValidators<T[K][K2][K3][K4][K5][K6][K7]>;
  // protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8): CustomValidators<T[K][K2][K3][K4][K5][K6][K7][K8]>;
  // protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9): CustomValidators<T[K][K2][K3][K4][K5][K6][K7][K8][K9]>;
  // protected findValidators<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8], K10 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8][K9]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9, k10: K10): CustomValidators<T[K][K2][K3][K4][K5][K6][K7][K8][K9][K10]>;
  /* tslint:enable:max-line-length */

  /** @deprecated **/
  protected findValidators(...path: Array<string | number>): CustomValidators<any> {
    let value: CustomValidators<T> = this.getValidators(true);
    if (!value) {
      return null;
    }

    const [pathEl] = path;
    if (pathEl && pathEl.toString().includes('.')) {
      throw Error(`Path string cannot include dot character: ${pathEl}`);
    }

    for (const key of path) {
      if (value.children && value.children.hasOwnProperty(key)) {
        value = value.children[key];
      } else {
        console.error(`No validator with specified path: '${path}'`);
        return null;
      }
    }
    return value;
  }

  protected abstract getValidators(editMode?: boolean): CustomValidators<T>;
}
